/* component-deferred-media.js — <deferred-media>
   Click-to-load facade for heavy media (video / external video / model).
   Renders a poster + play button; the real media lives inside a <template>
   and is injected only on first activation. Keeps PDP LCP light — no iframe
   or <video> in the DOM until the user asks for it.

   Markup:
     <deferred-media data-media-type="video">
       <button type="button" data-deferred-poster>
         <img ...poster...>
         <span class="deferred-media__play">▶</span>
       </button>
       <template data-deferred-content>
         <video controls>...</video>   (or iframe / model-viewer)
       </template>
     </deferred-media>

   Autoloads (no click) when data-autoload is present.  */
class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this._poster = this.querySelector('[data-deferred-poster]');
    this._template = this.querySelector('template[data-deferred-content]');
    this._loaded = false;
    if (this._poster) {
      this._onClick = () => this.load();
      this._poster.addEventListener('click', this._onClick);
    }
    if (this.hasAttribute('data-autoload')) this.load();
  }

  disconnectedCallback() {
    if (this._poster && this._onClick) this._poster.removeEventListener('click', this._onClick);
  }

  load() {
    if (this._loaded || !this._template) return;
    this._loaded = true;
    const content = this._template.content.cloneNode(true);
    this._template.replaceWith(content);
    if (this._poster) this._poster.setAttribute('hidden', '');
    // Try to autoplay a freshly injected <video>.
    const media = this.querySelector('video, iframe, model-viewer');
    if (media) {
      if (media.tagName === 'VIDEO' && typeof media.play === 'function') {
        media.play().catch(function () {});
      }
      if (typeof media.focus === 'function') media.focus();
    }
    this.dispatchEvent(new CustomEvent('deferred-media:loaded', { bubbles: true }));
  }
}

if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);
